import { action, observable, computed, reaction } from 'mobx'
import authenticationStore from './authenticationStore'
import { NewsFeedRequest } from '../requests/NewsFeedRequest'
import { apiUrl, apiWsUrl } from '../config'
import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'

class NewsFeedStore {
  constructor() {
    // Reconnect STOMP client whenever the app JWT token changes
    try {
      reaction(
        () => authenticationStore.appToken,
        () => {
          // Force reconnect so WS uses the latest token
          this.disconnectAllSSE()
          this._ensureStompClient()
        }
      )
    } catch (e) {
      console.error('Failed to setup token reaction in NewsFeedStore', e)
    }
  }
  // ==========================================
  // FEED POSTS
  // ==========================================
  @observable activeFeedType = 'feed'
  @observable feeds = {
    feed: {
      posts: [],
      page: 0,
      hasMore: true,
      totalCount: 0,
      initialized: false,
    },
    bookmarks: {
      posts: [],
      page: 0,
      hasMore: true,
      totalCount: 0,
      initialized: false,
    },
    hidden: {
      posts: [],
      page: 0,
      hasMore: true,
      totalCount: 0,
      initialized: false,
    },
  }

  @computed get feedPosts() {
    return this.feeds[this.activeFeedType].posts
  }
  @computed get feedTotalCount() {
    return this.feeds[this.activeFeedType].totalCount
  }
  @computed get feedPage() {
    return this.feeds[this.activeFeedType].page
  }
  @computed get feedHasMore() {
    return this.feeds[this.activeFeedType].hasMore
  }

  @observable feedSize = 10
  @observable feedLoading = false

  @observable currentPost = null

  // ==========================================
  // COMMENTS (per post)
  // ==========================================
  @observable commentsMap = {} // { postId: { comments: [], page, totalCount, hasMore } }
  @observable repliesMap = {} // { commentId: { replies: [], page, totalCount, hasMore } }

  // ==========================================
  // NOTIFICATIONS
  // ==========================================
  @observable nfNotifications = []
  @observable nfUnreadCount = 0
  @observable nfNotifPage = 0
  @observable nfNotifTotalCount = 0

  // ==========================================
  // WebSocket (STOMP) Connections
  // ==========================================
  wsClient = null // Single shared STOMP client
  wsSubscriptions = {} // { postId: StompSubscription }
  wsRefCount = {} // { postId: Number }
  _wsPendingCallbacks = [] // Callbacks waiting for connection
  _wsToken = ''

  // ==========================================
  // POST ACTIONS
  // ==========================================

  /** Lấy danh sách feed (infinite scroll - append) */
  @action getFeed = (reset = false, feedType = 'feed') => {
    let actualType = feedType
    if (typeof feedType === 'boolean') {
      actualType = feedType ? 'bookmarks' : 'feed'
    }

    if (!this.feeds[actualType]) {
      actualType = 'feed'
    }

    this.activeFeedType = actualType

    // Compute to cache: If it's a tab switch (reset=true) and we already have data, just return the cached data!
    if (reset && this.feeds[actualType].initialized) {
      return Promise.resolve({
        data: {
          data: this.feeds[actualType].posts,
          total_count: this.feeds[actualType].totalCount,
        },
      })
    }

    if (reset) {
      this.feeds[actualType].posts = []
      this.feeds[actualType].page = 0
      this.feeds[actualType].hasMore = true
    }
    this.feedLoading = true

    const pageSize =
      actualType === 'bookmarks' || actualType === 'hidden' ? 3 : this.feedSize

    let requestPromise
    if (actualType === 'bookmarks') {
      requestPromise = NewsFeedRequest.getBookmarkedFeed(
        this.feedPage,
        pageSize
      )
    } else if (actualType === 'hidden') {
      requestPromise = NewsFeedRequest.getHiddenFeed(this.feedPage, pageSize)
    } else {
      requestPromise = NewsFeedRequest.getFeed(this.feedPage, pageSize)
    }

    return new Promise((resolve, reject) => {
      requestPromise
        .then(response => {
          const { data, total_count } = response.data
          const currentFeed = this.feeds[actualType]

          if (reset) {
            currentFeed.posts = data || []
          } else {
            currentFeed.posts = [...currentFeed.posts, ...(data || [])]
          }
          currentFeed.totalCount = total_count || 0
          currentFeed.hasMore =
            currentFeed.posts.length < currentFeed.totalCount
          currentFeed.page = currentFeed.page + 1
          currentFeed.initialized = true

          resolve(response)
        })
        .catch(error => reject(error))
        .finally(() => {
          this.feedLoading = false
        })
    })
  }

  /** Tạo bài viết mới */
  @action createPost = formData => {
    return new Promise((resolve, reject) => {
      NewsFeedRequest.createPost(formData)
        .then(response => {
          // Thêm bài mới vào đầu feed tab "feed"
          if (response.data) {
            const feedState = this.feeds['feed']
            feedState.posts = [response.data, ...feedState.posts]
            feedState.totalCount += 1
          }
          resolve(response)
        })
        .catch(error => reject(error))
    })
  }

  /** Lấy chi tiết 1 bài viết */
  @action getPostById = postId => {
    return new Promise((resolve, reject) => {
      NewsFeedRequest.getPostById(postId)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => reject(error))
    })
  }

  /** Cập nhật bài viết */
  @action updatePost = (postId, formData) => {
    return new Promise((resolve, reject) => {
      NewsFeedRequest.updatePost(postId, formData)
        .then(response => {
          if (response.data) {
            // Cập nhật bài viết trong tất cả các tab
            Object.keys(this.feeds).forEach(key => {
              const index = this.feeds[key].posts.findIndex(
                p => p.id === postId
              )
              if (index !== -1) {
                this.feeds[key].posts[index] = response.data
                this.feeds[key].posts = [...this.feeds[key].posts]
              }
            })
          }
          resolve(response)
        })
        .catch(error => reject(error))
    })
  }

  /** Lấy danh sách người đã like bài viết */
  @action getPostLikes = postId => {
    return new Promise((resolve, reject) => {
      NewsFeedRequest.getLikesPost(postId)
        .then(response => {
          resolve(response.data || [])
        })
        .catch(error => reject(error))
    })
  }

  /** Lấy danh sách người đã like bình luận */
  @action getCommentLikes = commentId => {
    return new Promise((resolve, reject) => {
      NewsFeedRequest.getLikesComment(commentId)
        .then(response => {
          resolve(response.data || [])
        })
        .catch(error => reject(error))
    })
  }

  /** Lấy danh sách media của tôi */
  @action getMyMedia = () => {
    return new Promise((resolve, reject) => {
      NewsFeedRequest.getMyMedia()
        .then(response => {
          resolve(response.data || [])
        })
        .catch(error => reject(error))
    })
  }

  /** Xóa bài viết */
  @action deletePost = postId => {
    return new Promise((resolve, reject) => {
      NewsFeedRequest.deletePost(postId)
        .then(response => {
          Object.keys(this.feeds).forEach(key => {
            const initialLength = this.feeds[key].posts.length
            this.feeds[key].posts = this.feeds[key].posts.filter(
              p => p.id !== postId
            )
            if (this.feeds[key].posts.length < initialLength) {
              this.feeds[key].totalCount -= 1
            }
          })
          resolve(response)
        })
        .catch(error => reject(error))
    })
  }

  /** Ẩn bài viết */
  @action hidePost = postId => {
    return new Promise((resolve, reject) => {
      NewsFeedRequest.hidePost(postId)
        .then(response => {
          // Remove from feed, but might be added to hidden tab if initialized
          const initialLength = this.feeds['feed'].posts.length
          this.feeds['feed'].posts = this.feeds['feed'].posts.filter(
            p => p.id !== postId
          )
          if (this.feeds['feed'].posts.length < initialLength) {
            this.feeds['feed'].totalCount -= 1
          }

          // Re-fetch hidden tab if we visit it to get updated list
          this.feeds['hidden'].initialized = false

          resolve(response)
        })
        .catch(error => reject(error))
    })
  }

  /** Mở lại bài viết đã ẩn */
  @action unhidePost = postId => {
    return new Promise((resolve, reject) => {
      NewsFeedRequest.unhidePost(postId)
        .then(response => {
          const initialLength = this.feeds['hidden'].posts.length
          this.feeds['hidden'].posts = this.feeds['hidden'].posts.filter(
            p => p.id !== postId
          )
          if (this.feeds['hidden'].posts.length < initialLength) {
            this.feeds['hidden'].totalCount -= 1
          }

          // Re-fetch feed tab if we visit it
          this.feeds['feed'].initialized = false

          resolve(response)
        })
        .catch(error => reject(error))
    })
  }

  // ==========================================
  // LIKE ACTIONS (Optimistic Update)
  // ==========================================

  /** Toggle like bài viết - optimistic update */
  @action toggleLikePost = (postId, reaction) => {
    // Optimistic update UI ngay lập tức
    let previous = null
    const nextLiked = reaction !== null && reaction !== undefined

    Object.keys(this.feeds).forEach(key => {
      const post = this.feeds[key].posts.find(p => p.id === postId)
      if (post) {
        if (!previous) {
          previous = {
            is_liked: Boolean(post.is_liked),
            like_count: post.like_count || 0,
          }
        }
        post.is_liked = nextLiked
        if (!previous.is_liked && nextLiked) {
          post.like_count = previous.like_count + 1
        } else if (previous.is_liked && !nextLiked) {
          post.like_count = Math.max(0, previous.like_count - 1)
        } else {
          post.like_count = previous.like_count
        }
        this.feeds[key].posts = [...this.feeds[key].posts]
      }
    })

    // Gọi API sau
    return new Promise((resolve, reject) => {
      NewsFeedRequest.toggleLikePost(postId, reaction)
        .then(response => resolve(response))
        .catch(error => {
          // Revert nếu fail
          if (previous) {
            Object.keys(this.feeds).forEach(key => {
              const post = this.feeds[key].posts.find(p => p.id === postId)
              if (post) {
                post.is_liked = previous.is_liked
                post.like_count = previous.like_count
                this.feeds[key].posts = [...this.feeds[key].posts]
              }
            })
          }
          reject(error)
        })
    })
  }

  /** Toggle bookmark bài viết - optimistic update */
  @action toggleBookmarkPost = (postId, isBookmarksTab = false) => {
    Object.keys(this.feeds).forEach(key => {
      const post = this.feeds[key].posts.find(p => p.id === postId)
      if (post) {
        post.is_bookmarked = !post.is_bookmarked
        if (key === 'bookmarks' && !post.is_bookmarked) {
          this.feeds[key].posts = this.feeds[key].posts.filter(
            p => p.id !== postId
          )
        } else {
          this.feeds[key].posts = [...this.feeds[key].posts]
        }
      }
    })

    return new Promise((resolve, reject) => {
      NewsFeedRequest.toggleBookmarkPost(postId)
        .then(response => resolve(response))
        .catch(error => {
          // Revert nếu fail
          Object.keys(this.feeds).forEach(key => {
            const postRevert = this.feeds[key].posts.find(p => p.id === postId)
            if (postRevert) {
              postRevert.is_bookmarked = !postRevert.is_bookmarked
              this.feeds[key].posts = [...this.feeds[key].posts]
            }
          })
          if (isBookmarksTab) {
            this.feeds['bookmarks'].initialized = false // Force refetch on revisit
          }
          reject(error)
        })
    })
  }

  // ==========================================
  // COMMENT ACTIONS
  // ==========================================

  getCommentMapKey = (postId, mediaId = null) => {
    if (mediaId != null && mediaId !== '') {
      return `${postId}:media:${mediaId}`
    }
    return String(postId)
  }

  /** Lấy comments root cho 1 bài viết (hoặc 1 ảnh nếu có mediaId) */
  @action getComments = (postId, reset = false, mediaId = null) => {
    const key = this.getCommentMapKey(postId, mediaId)
    if (!this.commentsMap[key] || reset) {
      this.commentsMap[key] = {
        comments: [],
        page: 0,
        totalCount: 0,
        hasMore: true,
        loading: false,
      }
    }
    const state = this.commentsMap[key]
    if (reset) {
      state.comments = []
      state.page = 0
      state.hasMore = true
    }
    state.loading = true

    return new Promise((resolve, reject) => {
      NewsFeedRequest.getComments(postId, state.page, 5, mediaId)
        .then(response => {
          const { data, total_count } = response.data
          const normalizedComments = (data || []).map(item =>
            this.normalizeCommentObject(item)
          )

          if (reset) {
            state.comments = normalizedComments
          } else {
            // Merge existing and newly loaded comments, dedupe by id
            const all = [...state.comments, ...normalizedComments]
            const unique = []
            const seen = new Set()
            for (const c of all) {
              const cid = this.getCommentId(c)
              if (cid == null) continue
              if (!seen.has(String(cid))) {
                seen.add(String(cid))
                unique.push(this.normalizeCommentObject(c))
              }
            }
            // Keep chronological order (oldest first)
            unique.sort(
              (a, b) =>
                new Date(a.created_at || a.createdAt) -
                new Date(b.created_at || b.createdAt)
            )
            state.comments = unique
          }
          state.totalCount = total_count || 0
          state.hasMore = state.comments.length < state.totalCount
          state.page = state.page + 1
          state.loading = false
          // Trigger re-render
          this.commentsMap = { ...this.commentsMap }
          resolve(response)
        })
        .catch(error => {
          state.loading = false
          reject(error)
        })
    })
  }

  /** Lấy replies cho 1 comment */
  @action getReplies = commentId => {
    if (!this.repliesMap[commentId]) {
      this.repliesMap[commentId] = {
        replies: [],
        page: 0,
        totalCount: 0,
        hasMore: true,
        loading: false,
      }
    }
    const state = this.repliesMap[commentId]
    state.loading = true

    return new Promise((resolve, reject) => {
      NewsFeedRequest.getReplies(commentId, state.page, 3)
        .then(response => {
          const { data, total_count } = response.data
          const allReplies = [
            ...state.replies,
            ...(data || []).map(item => this.normalizeCommentObject(item)),
          ]
          const uniqueReplies = []
          const seen = new Set()
          for (const r of allReplies) {
            const replyId = this.getCommentId(r)
            if (replyId != null && !seen.has(replyId)) {
              seen.add(replyId)
              uniqueReplies.push(this.normalizeCommentObject(r))
            }
          }
          uniqueReplies.sort(
            (a, b) =>
              new Date(a.created_at || a.createdAt) -
              new Date(b.created_at || b.createdAt)
          )
          state.replies = uniqueReplies

          state.totalCount = total_count || 0
          state.hasMore = state.replies.length < state.totalCount
          state.page = state.page + 1
          state.loading = false
          this.repliesMap = { ...this.repliesMap }
          resolve(response)
        })
        .catch(error => {
          state.loading = false
          reject(error)
        })
    })
  }

  getCommentId = comment => {
    if (!comment) return null
    return comment.id || comment.comment_id || comment.commentId || null
  }

  normalizeCommentObject = comment => {
    if (!comment) return null
    const normalized = { ...comment }
    const id = this.getCommentId(comment)
    if (id != null) {
      normalized.id = Number(id)
    }
    return normalized
  }

  /** Tạo comment mới (data có thể chứa media_id) */
  @action createComment = (postId, data) => {
    const isFormData = data instanceof FormData
    const parentId = isFormData
      ? data.get('parent_id')
      : data?.parent_id || data?.parentId
    const mediaIdFromPayload = isFormData
      ? data.get('media_id')
      : data?.media_id || data?.mediaId
    const mapKey = this.getCommentMapKey(
      postId,
      mediaIdFromPayload != null && mediaIdFromPayload !== ''
        ? mediaIdFromPayload
        : null
    )

    return new Promise((resolve, reject) => {
      NewsFeedRequest.createComment(postId, data)
        .then(response => {
          const newComment = this.normalizeCommentObject(response.data)
          const newCommentId = this.getCommentId(newComment)
          if (!newComment || !newCommentId) {
            resolve(response)
            return
          }
          if (newComment) {
            const resolvedParentId =
              parentId || newComment.parent_id || newComment.parentId
            const resolvedMediaId =
              newComment.media_id ||
              newComment.mediaId ||
              mediaIdFromPayload ||
              null
            const commentKey = this.getCommentMapKey(postId, resolvedMediaId)

            if (resolvedParentId) {
              // Reply: thêm vào repliesMap
              let initialTotalCount = 0
              const parentState = this.commentsMap[commentKey]
              if (parentState && parentState.comments) {
                const parentComment = parentState.comments.find(
                  c => String(c.id) === String(resolvedParentId)
                )
                if (parentComment) {
                  initialTotalCount = parentComment.reply_count || 0
                }
              }

              if (!this.repliesMap[resolvedParentId]) {
                this.repliesMap[resolvedParentId] = {
                  replies: [],
                  page: 0,
                  totalCount: initialTotalCount,
                  hasMore: false,
                  loading: false,
                }
              }
              const replies = this.repliesMap[resolvedParentId].replies
              if (
                !replies.find(
                  r => String(this.getCommentId(r)) === String(newCommentId)
                )
              ) {
                this.repliesMap[resolvedParentId].replies = [
                  ...replies,
                  newComment,
                ]
                this.repliesMap[resolvedParentId].totalCount += 1
                this.repliesMap = { ...this.repliesMap }
              }

              // Update reply_count trên parent comment
              if (parentState && parentState.comments) {
                const index = parentState.comments.findIndex(
                  c => String(c.id) === String(resolvedParentId)
                )
                if (index !== -1) {
                  parentState.comments[index] = {
                    ...parentState.comments[index],
                    reply_count:
                      (parentState.comments[index].reply_count || 0) + 1,
                  }
                  parentState.comments = [...parentState.comments]
                  this.commentsMap = { ...this.commentsMap }
                }
              }
            } else {
              // Root comment: thêm vào commentsMap (post hoặc media)
              if (!this.commentsMap[commentKey]) {
                this.commentsMap[commentKey] = {
                  comments: [],
                  page: 0,
                  totalCount: 0,
                  hasMore: false,
                  loading: false,
                }
              }
              const comments = this.commentsMap[commentKey].comments
              if (
                !comments.find(
                  c => String(this.getCommentId(c)) === String(newCommentId)
                )
              ) {
                this.commentsMap[commentKey].comments = [
                  ...comments,
                  newComment,
                ]
                this.commentsMap[commentKey].totalCount += 1
                this.commentsMap = { ...this.commentsMap }
              }
            }

            // Update comment_count trên post
            Object.keys(this.feeds).forEach(key => {
              const post = this.feeds[key].posts.find(p => p.id === postId)
              if (post) {
                post.comment_count = (post.comment_count || 0) + 1
                this.feeds[key].posts = [...this.feeds[key].posts]
              }
            })
          }
          resolve(response)
        })
        .catch(error => reject(error))
    })
  }

  /** Xóa comment */
  @action deleteComment = (commentId, postId, parentId) => {
    return new Promise((resolve, reject) => {
      NewsFeedRequest.deleteComment(commentId)
        .then(response => {
          const touchCommentBuckets = updater => {
            Object.keys(this.commentsMap).forEach(key => {
              if (
                key === String(postId) ||
                key.startsWith(`${postId}:media:`)
              ) {
                updater(this.commentsMap[key], key)
              }
            })
            this.commentsMap = { ...this.commentsMap }
          }

          if (parentId) {
            // Xóa reply
            const replyState = this.repliesMap[parentId]
            if (replyState) {
              replyState.replies = replyState.replies.filter(
                r => r.id !== commentId
              )
              replyState.totalCount -= 1
              this.repliesMap = { ...this.repliesMap }
            }

            // Update reply_count trên parent comment (post hoặc media bucket)
            touchCommentBuckets(parentState => {
              if (!parentState?.comments) return
              const index = parentState.comments.findIndex(
                c => c.id === parentId
              )
              if (index !== -1) {
                parentState.comments[index] = {
                  ...parentState.comments[index],
                  reply_count: Math.max(
                    0,
                    (parentState.comments[index].reply_count || 0) - 1
                  ),
                }
                parentState.comments = [...parentState.comments]
              }
            })
          } else {
            // Xóa root comment khỏi bucket tương ứng
            touchCommentBuckets(commentState => {
              if (!commentState) return
              const before = commentState.comments.length
              commentState.comments = commentState.comments.filter(
                c => c.id !== commentId
              )
              if (commentState.comments.length < before) {
                commentState.totalCount = Math.max(
                  0,
                  (commentState.totalCount || 0) - 1
                )
              }
            })
          }

          // Update comment_count trên post
          Object.keys(this.feeds).forEach(key => {
            const post = this.feeds[key].posts.find(p => p.id === postId)
            if (post) {
              post.comment_count = Math.max(0, post.comment_count - 1)
              this.feeds[key].posts = [...this.feeds[key].posts]
            }
          })

          resolve(response)
        })
        .catch(error => reject(error))
    })
  }

  /** Toggle like comment */
  @action toggleLikeComment = (commentId, postId, parentId, reaction) => {
    const cid = Number(commentId)
    const nextLiked = reaction !== null && reaction !== undefined
    const snapshots = []

    // Helper to toggle like on a comment/reply object
    const toggleLikeState = item => ({
      ...item,
      is_liked: nextLiked,
      like_count:
        !item.is_liked && nextLiked
          ? (item.like_count || 0) + 1
          : item.is_liked && !nextLiked
          ? Math.max(0, (item.like_count || 0) - 1)
          : item.like_count || 0,
    })

    let found = false

    // Tìm và update trong commentsMap
    for (const [pId, state] of Object.entries(this.commentsMap)) {
      if (state && state.comments) {
        const index = state.comments.findIndex(c => Number(c.id) === cid)
        if (index !== -1) {
          snapshots.push({
            type: 'comment',
            key: pId,
            index,
            value: state.comments[index],
          })
          state.comments[index] = toggleLikeState(state.comments[index])
          state.comments = [...state.comments]
          this.commentsMap = { ...this.commentsMap }
          found = true
          break
        }
      }
    }

    // Nếu chưa tìm thấy, tìm và update trong repliesMap
    if (!found) {
      for (const [rId, state] of Object.entries(this.repliesMap)) {
        if (state && state.replies) {
          const index = state.replies.findIndex(r => Number(r.id) === cid)
          if (index !== -1) {
            snapshots.push({
              type: 'reply',
              key: rId,
              index,
              value: state.replies[index],
            })
            state.replies[index] = toggleLikeState(state.replies[index])
            state.replies = [...state.replies]
            this.repliesMap = { ...this.repliesMap }
            found = true
            break
          }
        }
      }
    }

    return NewsFeedRequest.toggleLikeComment(commentId, reaction).catch(
      error => {
        snapshots.forEach(snapshot => {
          if (snapshot.type === 'comment') {
            const state = this.commentsMap[snapshot.key]
            if (state && state.comments) {
              state.comments[snapshot.index] = snapshot.value
              state.comments = [...state.comments]
              this.commentsMap = { ...this.commentsMap }
            }
          } else {
            const state = this.repliesMap[snapshot.key]
            if (state && state.replies) {
              state.replies[snapshot.index] = snapshot.value
              state.replies = [...state.replies]
              this.repliesMap = { ...this.repliesMap }
            }
          }
        })
        throw error
      }
    )
  }

  // ==========================================
  // WebSocket (STOMP) - Real-time
  // ==========================================

  _ensureStompClient = () => {
    // Read current token from localStorage (may be updated by SSO refresh)
    let currentToken = ''
    try {
      const appTokenStr = localStorage.getItem('jwt')
      if (appTokenStr) {
        currentToken = JSON.parse(appTokenStr).access_token || ''
      }
    } catch (e) {
      console.error('Failed to parse jwt from localStorage', e)
    }

    // If a client exists but token changed, force a reconnect so WS uses fresh token
    if (this.wsClient && this._wsToken && this._wsToken !== currentToken) {
      try {
        this.wsClient.deactivate()
      } catch (e) {}
      this.wsClient = null
      this.wsSubscriptions = {}
      this.wsRefCount = {}
      this._wsPendingCallbacks = []
    }

    if (this.wsClient) return // already initialised (may still be connecting)

    const token = currentToken

    const baseWsUrl = apiWsUrl || apiUrl
    const wsUrl = token
      ? `${baseWsUrl}/api/v1/newsfeed/ws?access_token=${token}`
      : `${baseWsUrl}/api/v1/newsfeed/ws`

    // Save token used to create this client so we can detect changes later
    this._wsToken = token

    this.wsClient = new Client({
      webSocketFactory: () => new SockJS(wsUrl),
      reconnectDelay: 3000,
      onStompError: frame => {
        console.error('STOMP error:', frame)
      },
      onConnect: () => {
        // Flush all pending subscription callbacks
        const cbs = this._wsPendingCallbacks.splice(0)
        cbs.forEach(cb => cb())
      },
    })
    this.wsClient.activate()
  }

  /** Kết nối WebSocket cho real-time comments (tương thích tên cũ connectSSE) */
  @action connectSSE = postId => {
    this.wsRefCount[postId] = (this.wsRefCount[postId] || 0) + 1
    if (this.wsSubscriptions[postId]) return // Đã subscribe

    this._ensureStompClient()

    const subscribe = () => {
      if (this.wsSubscriptions[postId]) return // guard against double-subscribe
      const sub = this.wsClient.subscribe(
        `/topic/newsfeed/comments/${postId}`,
        message => {
          try {
            const payload = JSON.parse(message.body)
            const type = payload.type

            if (type === 'NEW_COMMENT') {
              this.handleSSENewComment(postId, payload.comment)
            } else if (type === 'DELETE_COMMENT') {
              this.handleSSEDeleteComment(
                postId,
                payload.commentId,
                payload.parentId
              )
            } else if (type === 'LIKE_UPDATE') {
              this.handleSSELikeUpdate(
                postId,
                payload.commentId,
                payload.likeCount
              )
            } else if (type === 'POST_LIKE_UPDATE') {
              this.handleSSEPostLikeUpdate(
                payload.post_id || postId,
                payload.like_count
              )
            }
          } catch (e) {
            console.error('WS message parse error:', e)
          }
        }
      )
      this.wsSubscriptions[postId] = sub
    }

    if (this.wsClient.connected) {
      subscribe()
    } else {
      // Enqueue for when the connection is established
      this._wsPendingCallbacks.push(subscribe)
    }
  }

  /** Ngắt kết nối WebSocket (tương thích tên cũ disconnectSSE) */
  @action disconnectSSE = postId => {
    this.wsRefCount[postId] = Math.max(0, (this.wsRefCount[postId] || 1) - 1)
    if (this.wsRefCount[postId] === 0 && this.wsSubscriptions[postId]) {
      try {
        this.wsSubscriptions[postId].unsubscribe()
      } catch (e) {}
      delete this.wsSubscriptions[postId]
    }
  }

  /** Ngắt tất cả WebSocket subscriptions */
  @action disconnectAllSSE = () => {
    Object.keys(this.wsSubscriptions).forEach(postId => {
      try {
        this.wsSubscriptions[postId].unsubscribe()
      } catch (e) {}
    })
    this.wsSubscriptions = {}
    this.wsRefCount = {}
    if (this.wsClient) {
      try {
        this.wsClient.deactivate()
      } catch (e) {}
      this.wsClient = null
    }
  }

  /** Handle SSE new comment event */
  @action handleSSENewComment = (postId, comment) => {
    const normalizedComment = this.normalizeCommentObject(comment)
    if (!normalizedComment) return
    const commentId = this.getCommentId(normalizedComment)
    if (!commentId) return
    if (comment.parent_id) {
      // Reply
      if (!this.repliesMap[comment.parent_id]) {
        this.repliesMap[comment.parent_id] = {
          replies: [],
          page: 0,
          totalCount: 0,
          hasMore: false,
          loading: false,
        }
      }
      const replies = this.repliesMap[comment.parent_id].replies
      if (
        !replies.find(r => String(this.getCommentId(r)) === String(commentId))
      ) {
        this.repliesMap[comment.parent_id].replies = [
          ...replies,
          normalizedComment,
        ]
        this.repliesMap[comment.parent_id].totalCount += 1
        this.repliesMap = { ...this.repliesMap }
      }
    } else {
      // Root comment (post-level hoặc media-level)
      const mediaId =
        normalizedComment.media_id || normalizedComment.mediaId || null
      const key = this.getCommentMapKey(postId, mediaId)
      if (!this.commentsMap[key]) {
        this.commentsMap[key] = {
          comments: [],
          page: 0,
          totalCount: 0,
          hasMore: false,
          loading: false,
        }
      }
      const comments = this.commentsMap[key].comments
      if (
        !comments.find(c => String(this.getCommentId(c)) === String(commentId))
      ) {
        this.commentsMap[key].comments = [...comments, normalizedComment]
        this.commentsMap[key].totalCount += 1
        this.commentsMap = { ...this.commentsMap }
      }
    }
  }

  /** Handle SSE delete comment event */
  @action handleSSEDeleteComment = (postId, commentId, parentId) => {
    if (parentId) {
      const replyState = this.repliesMap[parentId]
      if (replyState) {
        replyState.replies = replyState.replies.filter(r => r.id !== commentId)
        this.repliesMap = { ...this.repliesMap }
      }
    } else {
      // Xóa khỏi mọi bucket comment của post (post + media)
      Object.keys(this.commentsMap).forEach(key => {
        if (key === String(postId) || key.startsWith(`${postId}:media:`)) {
          const commentState = this.commentsMap[key]
          if (commentState) {
            commentState.comments = commentState.comments.filter(
              c => c.id !== commentId
            )
          }
        }
      })
      this.commentsMap = { ...this.commentsMap }
    }
  }

  /** Handle SSE post like update event */
  @action handleSSEPostLikeUpdate = (postId, likeCount) => {
    const pid = Number(postId)
    Object.keys(this.feeds).forEach(key => {
      const post = this.feeds[key].posts.find(p => p.id === pid)
      if (post) {
        post.like_count = likeCount
        post.reaction_updated_at = Date.now()
        this.feeds[key].posts = [...this.feeds[key].posts]
      }
    })
  }

  /** Handle SSE like update event (comment) */
  @action handleSSELikeUpdate = (postId, commentId, likeCount) => {
    const cid = Number(commentId)
    let found = false
    // Tìm trong commentsMap
    for (const [pId, state] of Object.entries(this.commentsMap)) {
      if (state && state.comments) {
        const index = state.comments.findIndex(c => Number(c.id) === cid)
        if (index !== -1) {
          state.comments[index] = {
            ...state.comments[index],
            like_count: likeCount,
            reaction_updated_at: Date.now(),
          }
          state.comments = [...state.comments]
          found = true
          break
        }
      }
    }
    // Tìm trong repliesMap
    if (!found) {
      for (const [rId, state] of Object.entries(this.repliesMap)) {
        if (state && state.replies) {
          const index = state.replies.findIndex(r => Number(r.id) === cid)
          if (index !== -1) {
            state.replies[index] = {
              ...state.replies[index],
              like_count: likeCount,
              reaction_updated_at: Date.now(),
            }
            state.replies = [...state.replies]
            found = true
            break
          }
        }
      }
    }
    if (found) {
      this.commentsMap = { ...this.commentsMap }
      this.repliesMap = { ...this.repliesMap }
    }
  }

  // ==========================================
  // NOTIFICATIONS
  // ==========================================

  /** Lấy danh sách thông báo */
  @action getNotifications = (reset = false) => {
    if (reset) {
      this.nfNotifications = []
      this.nfNotifPage = 0
    }
    return new Promise((resolve, reject) => {
      NewsFeedRequest.getNotifications(this.nfNotifPage, 20)
        .then(response => {
          const { data, total_count, total_unread } = response.data
          const mappedData = (data || []).map(item => ({
            id: item.id,
            type: item.type,
            message: item.content,
            created_at: item.time_created,
            is_read: item.status,
            post_id: item.postId,
            comment_id: item.commentId,
            actor_display_name: item.owner,
            actor_avatar_url: item.userImage
              ? `${apiUrl}/api/v1/images/${item.userImage}`
              : null,
          }))

          if (reset) {
            this.nfNotifications = mappedData
          } else {
            this.nfNotifications = [...this.nfNotifications, ...mappedData]
          }
          this.nfNotifTotalCount = total_count || 0
          this.nfUnreadCount = total_unread || 0
          this.nfNotifPage += 1
          resolve(response)
        })
        .catch(error => reject(error))
    })
  }

  /** Lấy số thông báo chưa đọc */
  @action getUnreadCount = () => {
    return NewsFeedRequest.getUnreadCount()
      .then(response => {
        this.nfUnreadCount = response.data?.total_unread || 0
      })
      .catch(() => {})
  }

  /** Đánh dấu tất cả đã đọc */
  @action markAllRead = () => {
    return NewsFeedRequest.markAllRead().then(() => {
      this.nfUnreadCount = 0
      this.nfNotifications = this.nfNotifications.map(n => ({
        ...n,
        is_read: true,
      }))
    })
  }

  /** Đánh dấu 1 thông báo đã đọc */
  @action markRead = notificationId => {
    return NewsFeedRequest.markRead(notificationId).then(() => {
      const notif = this.nfNotifications.find(n => n.id === notificationId)
      if (notif && !notif.is_read) {
        notif.is_read = true
        this.nfUnreadCount = Math.max(0, this.nfUnreadCount - 1)
      }
    })
  }

  // ==========================================
  // CLEAR STORE
  // ==========================================
  @action clearStore = () => {
    this.disconnectAllSSE()
    this.feeds = {
      feed: {
        posts: [],
        page: 0,
        hasMore: true,
        totalCount: 0,
        initialized: false,
      },
      bookmarks: {
        posts: [],
        page: 0,
        hasMore: true,
        totalCount: 0,
        initialized: false,
      },
      hidden: {
        posts: [],
        page: 0,
        hasMore: true,
        totalCount: 0,
        initialized: false,
      },
    }
    this.feedLoading = false
    this.currentPost = null
    this.commentsMap = {}
    this.repliesMap = {}
    this.nfNotifications = []
    this.nfUnreadCount = 0
    this.nfNotifPage = 0
  }
}

export default new NewsFeedStore()
